/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { BarItem, BarSeries } from './barChart.types';
import { Bar } from './Bar';
import { getBarProps, getRectCoord } from './utils/barItemUtils';
import { ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { getBarGapsInfo } from './utils/barSeriesUtils';
import { getBaseAndEndCoord } from './utils/barLayoutUtils';
import { Info, Scale } from '../UNSAFE_Chart';
import { Group } from '../UNSAFE_Axis/axis.types';
import { getGroupCenterCoord } from '../utils/PRIVATE_chartUtils/layoutUtils';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '../resources/nls/bundle';
import { getItemAriaLabel } from '../utils/PRIVATE_chartUtils/accUtils';
import { supportsMobileScreenReader } from '../utils/UNSAFE_visUtils';

type BarGroupProps<K, D> = {
  yScale: Scale;
  xScale: Scale;
  colors?: ColorProps['color'][];
  series: BarSeries<K, D>[];
  group: Group;
  timeAxisType?: 'enabled' | 'skipGaps' | 'mixedFrequency';
  focusedItemInfo?: Info;
  hoveredItemInfo?: Info;
  isSelectionEnabled: boolean;
  selectedIds?: Set<K>;
  hiddenIds?: Set<K>;
  highlightedIds?: Set<K>;
  groupIndex: number;
  barGapRatio: number;
  dataItemGaps: number;
  groupWidth: number;
  isStacked: boolean;
  activeId?: string;
  orientation: 'horizontal' | 'vertical';
  isDrillEnabled: boolean;
  isLog: boolean;
  isRtl: boolean;
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined;
};

export function BarGroup<K extends string | number, D extends BarItem<K>>({
  yScale,
  xScale,
  colors,
  series,
  groupIndex,
  hiddenIds,
  barGapRatio,
  dataItemGaps,
  groupWidth,
  highlightedIds,
  selectedIds,
  orientation,
  isLog,
  activeId,
  getDataItem,
  isRtl,
  isStacked,
  isSelectionEnabled,
  focusedItemInfo,
  hoveredItemInfo,
  isDrillEnabled,
  group,
  timeAxisType
}: BarGroupProps<K, D>) {
  const isHoriz = orientation === 'horizontal';
  const items = [];
  const numSeries = series.length;
  let posCumulativeValue = isLog ? yScale.domain()[0] : 0;
  let negCumulativeValue = 0;

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  for (let seriesIndex = 0; seriesIndex < numSeries; seriesIndex += 1) {
    const item = getDataItem(seriesIndex, groupIndex);
    if (!item || hiddenIds?.has(item.id) || (isLog && item.value <= 0)) {
      continue;
    }

    const groupCenter = getGroupCenterCoord(group, groupIndex, xScale, item, timeAxisType);
    const isFocused =
      focusedItemInfo?.groupIndex === groupIndex &&
      focusedItemInfo?.seriesIndex === seriesIndex &&
      focusedItemInfo?.isFocusVisible;
    const isHovered =
      hoveredItemInfo?.groupIndex === groupIndex &&
      hoveredItemInfo?.seriesIndex === seriesIndex &&
      hoveredItemInfo?.isCurrent;
    const isFocusVisible = !!isFocused || (!!isHovered && isSelectionEnabled);
    const isActive = (isFocused && focusedItemInfo.isCurrent) || isHovered;
    const isHighlighted = highlightedIds === undefined || highlightedIds.has(item.id);
    const isSelected = !!selectedIds?.has(item.id!);
    const isDrillable = item.drilling === 'on' || (item.drilling != 'off' && isDrillEnabled);

    const { barWidth, offset, itemsGaps } = getBarGapsInfo(
      seriesIndex,
      groupWidth,
      barGapRatio,
      numSeries,
      dataItemGaps,
      isStacked,
      isRtl
    );
    const x1 = groupCenter + offset;
    const x2 = x1 + barWidth;

    const { baselineCoord, endCoord } = getBaseAndEndCoord(
      item.value,
      yScale,
      isStacked,
      isLog,
      itemsGaps,
      negCumulativeValue,
      posCumulativeValue
    );

    negCumulativeValue += item.value < 0 ? item.value : 0;
    posCumulativeValue += item.value > 0 ? item.value : 0;

    const barprops = getBarProps(item, series[seriesIndex], colors?.[seriesIndex]);
    const rectParam = getRectCoord(x1, x2, endCoord, baselineCoord, isHoriz);
    const ariaLabel =
      supportsMobileScreenReader || isActive
        ? getItemAriaLabel(
            translations,
            series[seriesIndex].name || series[seriesIndex].id.toString(),
            group.name || group.id,
            item.value,
            isSelectionEnabled,
            isSelected,
            isDrillable,
            item.accessibleLabel
          )
        : '';

    items.push(
      <Bar
        {...rectParam}
        {...barprops}
        seriesIndex={seriesIndex}
        groupIndex={groupIndex}
        isFocused={isFocusVisible}
        activeId={isActive ? activeId : ''}
        aria-label={ariaLabel}
        isDimmed={!isHighlighted}
        isSelected={isSelected}
        isDrillable={isDrillable}
        key={`${groupIndex};${seriesIndex}`}
      />
    );
  }
  return <>{items}</>;
}
